import { useEffect, useState, type JSX } from "react";
import { Button } from "../../../components";
import type { ReviewStepRisk } from "../../../data/chatQuery";
import { useRecordDrawer } from "../RecordDrawer";
import { ReviewActions } from "./ReviewActions";
import { DraftWithDeviation } from "./reviewText";
import { CompactReviewCard } from "./CompactReviewCard";
import type { ReviewCardProps } from "./reviewCardProps";

const RISK_LABEL: Record<ReviewStepRisk, string> = {
  high: "High risk",
  medium: "Medium risk",
  low: "Low risk",
};

export { htmlToText, DraftWithDeviation } from "./reviewText";

// [S-509h] One clause's review card — a pure function of the current step's DATA.
// Shows the clause number + name, the draft text with the deviating span struck
// through, the certified (L1) recommendation, the precedent (L2) card kept
// visually separate (never-blend), and the agent's one-line take. Below the body
// sit the fixed action bar (ReviewActions) and prev/next navigation. No clause is
// special-cased: what renders is whatever the step carries.
function ClassicReviewCard({
  current,
  walked,
  pending,
  decision,
  onDecide,
  onGoto,
  onTypeSubmit,
  onJumpToDraft,
  revising = false,
  hideNav = false,
}: ReviewCardProps): JSX.Element {
  const cur = current;
  const { openRecord } = useRecordDrawer();
  // [Type-as-Other] "Type" reveals an inline box IN this card (like the "Other"
  // choice in a multiple-choice prompt), rather than jumping focus to the main
  // composer. State is local and resets when the open clause changes, so a draft
  // can't bleed from one clause to the next.
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");
  useEffect(() => {
    setTyping(false);
    setTyped("");
  }, [cur.index]);
  const submitTyped = (): void => {
    const t = typed.trim();
    if (t.length === 0) return;
    onTypeSubmit(cur.index, cur.name, t);
    setTyping(false);
    setTyped("");
  };
  return (
    <div className="sa-rev__body" data-testid="review-card" data-category={cur.category}>
      <div className="sa-rev__clause">
        <span className="sa-rev__no">
          {String(cur.index + 1).padStart(2, "0")}
        </span>
        <span className="sa-rev__name" data-testid="review-clause-name">
          {cur.name}
        </span>
        {/* [completeness-first] Additional clauses (no certified counterpart) are
            marked so the reader knows why there's no "replace with" recommendation. */}
        {cur.category === "additional" && (
          <span className="sa-rev__cat is-additional" data-testid="review-category">
            Additional
          </span>
        )}
        {/* [review-add] A standard clause the draft lacks: Approve adds it. */}
        {cur.category === "missing" && (
          <span className="sa-rev__cat is-additional" data-testid="review-category">
            Missing
          </span>
        )}
        {/* [S-509h/M1] Triage risk for the clause under review. */}
        <span className={`sa-rev__risk is-${cur.risk}`}>{RISK_LABEL[cur.risk]}</span>
      </div>

      <div className="sa-rev__panel">
        <div className="sa-rev__panel-head">
          <span>In the draft</span>
          {onJumpToDraft && (
            <Button
              variant="bare"
              className="sa-rev__jump"
              onClick={onJumpToDraft}
              title="Show this clause in the draft"
            >
              Show in draft ↗
            </Button>
          )}
        </div>
        <div className="sa-rev__panel-body">
          {cur.category === "missing" ? (
            <span data-testid="review-missing-note">Not in your draft.</span>
          ) : (
            <DraftWithDeviation text={cur.draftText} span={cur.deviationSpan} />
          )}
        </div>
      </div>

      {/* [completeness-first / P3] An addition that COUNTERS a standard clause —
          the only time an additional clause is a problem. */}
      {cur.conflicts.length > 0 && (
        <div className="sa-rev__conflict">
          <div className="sa-rev__conflict-head">
            <span className="sa-rev__conflict-dot" aria-hidden="true" />
            Conflicts with the standard
          </div>
          <ul className="sa-rev__conflict-list">
            {cur.conflicts.map((c, i) => (
              <li key={`${c.standardClauseName}-${i}`} className="sa-rev__conflict-item">
                <span className="sa-rev__conflict-name">{c.standardClauseName}</span>
                {" — "}
                {c.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* [completeness-first] An additional clause with no certified counterpart:
          say so plainly instead of leaving a blank where a recommendation sits. */}
      {/* [2026-10-21] Far outside comparable deals — nothing to apply. */}
      {cur.outlier != null && (
        <div className="sa-rev__conflict" data-testid="review-outlier">
          <div className="sa-rev__conflict-head">
            <span className="sa-rev__conflict-dot" aria-hidden="true" />
            Unusual against comparable deals
          </div>
          <div>{cur.outlier.summary}</div>
        </div>
      )}

      {cur.category === "additional" && cur.recommendation === null && cur.outlier == null && (
        <div className="sa-rev__additional" data-testid="review-additional-note">
          {"Additional clause — the certified standard doesn’t cover this."}
          {cur.conflicts.length === 0
            ? " That’s fine on its own; keep it or decide as you like."
            : " Resolve the conflict above before keeping it."}
        </div>
      )}

      {cur.recommendation !== null && (
        <div className="sa-rev__reco" data-testid="review-recommendation">
          <div className="sa-rev__reco-head">
            <span className="sa-rev__reco-dot" aria-hidden="true" />
            {cur.recommendation.action === "remove"
              ? "Recommendation · Remove this clause"
              : cur.recommendation.action === "add"
                ? "Recommendation · Add this clause"
                : "Recommendation · Certified"}
          </div>
          <div className="sa-rev__reco-body" data-testid="review-recommendation-text">
            {cur.recommendation.action === "remove"
              ? (cur.recommendation.whyPlain ?? "Apply change removes this clause from the draft.")
              : cur.recommendation.text}
          </div>
          {/* [Fix 5] The source chip opens the actual certified template
              (view-only) in the rail when the recommendation carries its id. */}
          {cur.recommendation.templateId != null ? (
            <Button
              variant="bare"
              className="sa-rev__reco-chip sa-rev__reco-chip--link"
              title="Open the certified template"
              onClick={() => {
                const rec = cur.recommendation;
                if (rec?.templateId == null) return;
                openRecord({
                  type: "template",
                  id: rec.templateId,
                  tenantId: null,
                  title: rec.sourceLabel,
                  uri: `csai://template/global/${rec.templateId}`,
                });
              }}
            >
              {cur.recommendation.sourceLabel} ↗
            </Button>
          ) : (
            <div className="sa-rev__reco-chip">{cur.recommendation.sourceLabel}</div>
          )}
          <div className="sa-rev__reco-why">Why: {cur.recommendation.why}</div>
          {/* [P4] Heads-up when applying this certified text as-is would clash with
              the draft's own wording (mismatched defined terms, dropped specifics). */}
          {cur.recommendation.coherenceNote != null &&
            cur.recommendation.coherenceNote.length > 0 && (
              <div className="sa-rev__reco-coherence">
                Heads-up: {cur.recommendation.coherenceNote}
              </div>
            )}
        </div>
      )}

      {/* [S-509g] L2 precedent facts — kept VISUALLY SEPARATE from the certified
          (L1) recommendation above (its own purple stud accent, distinct card) so
          the platform's never-blend layer language holds. Only renders when the
          backend attached precedent facts. */}
      {cur.recommendation?.precedent != null && (
        <div className="sa-rev__precedent">
          <div className="sa-rev__precedent-head">
            <span className="sa-rev__precedent-dot" aria-hidden="true" />
            Precedent — what we&rsquo;ve actually done
          </div>
          <div className="sa-rev__precedent-body">
            {cur.recommendation.precedent.summary}
          </div>
          {cur.recommendation.precedent.comparables.length > 0 && (
            <ul className="sa-rev__precedent-list">
              {cur.recommendation.precedent.comparables.map((c, i) => (
                <li key={`${c.title}-${i}`} className="sa-rev__precedent-item">
                  <span className="sa-rev__precedent-title">{c.title}</span>
                  {" — "}
                  <span className="sa-rev__precedent-term">{c.term}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* [S-509g] The agent's one-line strategic read of the precedent — visually
          distinct (accented, italic) so it's clearly an interpretation, not an
          L1/L2 fact. */}
      {cur.recommendation?.take != null && cur.recommendation.take.length > 0 && (
        <div className="sa-rev__take">{cur.recommendation.take}</div>
      )}

      {/* [Fix 3] Revise in flight → the recommendation is being rewritten; the
          card refreshes with the new text when it lands. */}
      {revising && (
        <div className="sa-rev__revising" data-testid="review-revising">
          Revising the recommendation…
        </div>
      )}

      <ReviewActions
        index={cur.index}
        pending={pending}
        onDecide={onDecide}
        onType={() => setTyping(true)}
        canApply={cur.recommendation !== null}
        applied={decision === "applied"}
        decisionNote={cur.decisionNote}
      />

      {/* [Type-as-Other] The inline "type your own" box, scoped to this clause —
          Enter sends, Shift+Enter newlines, Escape cancels. */}
      {typing && (
        <div className="sa-rev__type" data-testid="review-type">
          <textarea
            className="sa-rev__type-input"
            autoFocus
            rows={2}
            placeholder={`Tell the agent what to do with “${cur.name}”…`}
            value={typed}
            disabled={pending}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitTyped();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setTyping(false);
                setTyped("");
              }
            }}
          />
          <div className="sa-rev__type-acts">
            <Button
              variant="bare"
              className="sa-rev__btn sa-rev__btn--apply"
              disabled={pending || typed.trim().length === 0}
              onClick={submitTyped}
            >
              Send
            </Button>
            <Button
              variant="bare"
              className="sa-rev__btn sa-rev__btn--skip"
              disabled={pending}
              onClick={() => {
                setTyping(false);
                setTyped("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!hideNav && (() => {
        const prev = [...walked].reverse().find((i) => i < cur.index);
        const next = walked.find((i) => i > cur.index);
        return (
          <div className="sa-rev__nav">
            <Button
              variant="bare"
              className="sa-rev__navbtn"
              disabled={pending || prev === undefined}
              onClick={() => prev !== undefined && onGoto(prev)}
            >
              ← Clause {(prev ?? cur.index - 1) + 1}
            </Button>
            <Button
              variant="bare"
              className="sa-rev__navbtn"
              disabled={pending || next === undefined}
              onClick={() => next !== undefined && onGoto(next)}
            >
              Clause {(next ?? cur.index + 1) + 1} →
            </Button>
          </div>
        );
      })()}
    </div>
  );
}

// [review-compact] The compact card (inline word diff, one plain-language why line,
// a "Why" dialog with the evidence). Default ON; set localStorage
// `cs.reviewCompact` = "false" to restore this classic full card. Same pattern as
// `cs.reviewInline`.
export function reviewCompactEnabled(): boolean {
  try {
    // The compact card belongs to the inline model (it has no Prev/Next and relies
    // on stacked cards); the classic side pane (cs.reviewInline = "false") keeps the
    // classic card.
    return (
      window.localStorage.getItem("cs.reviewCompact") !== "false" &&
      window.localStorage.getItem("cs.reviewInline") !== "false"
    );
  } catch {
    return true;
  }
}

export function ReviewCard(props: ReviewCardProps): JSX.Element {
  return reviewCompactEnabled() ? (
    <CompactReviewCard {...props} />
  ) : (
    <ClassicReviewCard {...props} />
  );
}
